<template>
    <div>
        <inertia-head>
            <link rel="icon" type="image/png" href="/images/favicon.png" />
        </inertia-head>

        <div class="min-h-screen bg-gray-100">
            <nav class="bg-secondary sticky top-0 z-20">
                <!-- Primary Navigation Menu -->
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="flex justify-between h-16">
                        <div class="flex">
                            <!-- Logo -->
                            <div class="flex-shrink-0 flex items-center">
                                <inertia-link :href="route('main')">
                                    <application-logo
                                        class="block h-7 md:h-9 w-auto"
                                    />
                                </inertia-link>
                            </div>

                            <!-- Navigation Links -->
                            <div class="hidden sm:ml-10 sm:flex">
                                <nav-link :href="route('main')">
                                    Vehicle List
                                </nav-link>
                                <nav-link :href="route('contact')">
                                    Contact Us
                                </nav-link>
                            </div>
                        </div>

                        <div class="hidden sm:flex sm:items-center sm:ml-6">
                            <!-- Settings Dropdown -->
                            <div class="ml-3 relative">
                                <dropdown-component
                                    align="right"
                                    width="48"
                                    v-if="$page.props.auth.user"
                                >
                                    <template #trigger>
                                        <span class="inline-flex rounded-md">
                                            <button
                                                type="button"
                                                class="
                                                    inline-flex
                                                    items-center
                                                    px-3
                                                    py-2
                                                    border border-transparent
                                                    text-sm
                                                    leading-4
                                                    font-medium
                                                    rounded-md
                                                    text-gray-500
                                                    bg-white
                                                    hover:text-gray-700
                                                    focus:outline-none
                                                    transition
                                                    ease-in-out
                                                    duration-150
                                                "
                                            >
                                                {{ $page.props.auth.user.name }}

                                                <i
                                                    class="
                                                        icon-np-chevron-down
                                                        ml-2
                                                        -mr-0.5
                                                        text-base
                                                    "
                                                ></i>
                                            </button>
                                        </span>
                                    </template>

                                    <template #content>
                                        <dropdown-link
                                            :href="route('dashboard')"
                                            method="get"
                                            as="button"
                                        >
                                            User Panel
                                        </dropdown-link>
                                        <dropdown-link
                                            :href="route('logout')"
                                            method="post"
                                            as="button"
                                        >
                                            Log Out
                                        </dropdown-link>
                                    </template>
                                </dropdown-component>

                                <span v-else class="inline-flex rounded-md">
                                    <inertia-link
                                        :href="route('login')"
                                        class="
                                            inline-flex
                                            bg-white
                                            rounded-full
                                            text-sm text-primary
                                            px-10
                                            py-2
                                            font-medium
                                            tracking-wide
                                            focus:outline-none
                                            hover:bg-primary hover:text-white
                                            transition-all
                                            duration-500
                                        "
                                    >
                                        Log In
                                    </inertia-link>
                                </span>
                            </div>
                        </div>

                        <!-- Hamburger -->
                        <div class="-mr-2 flex items-center sm:hidden">
                            <button
                                @click="
                                    showingNavigationDropdown =
                                        !showingNavigationDropdown
                                "
                                class="
                                    inline-flex
                                    items-center
                                    justify-center
                                    p-2
                                    rounded-md
                                    text-gray-400
                                    hover:text-gray-500 hover:bg-gray-100
                                    focus:outline-none
                                    focus:bg-gray-100
                                    focus:text-gray-500
                                    transition
                                    duration-150
                                    ease-in-out
                                "
                            >
                                <i class="icon-hamburger-menu text-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Responsive Navigation Menu -->
                <div
                    :class="{
                        block: showingNavigationDropdown,
                        hidden: !showingNavigationDropdown,
                    }"
                    class="sm:hidden"
                >
                    <div class="pt-2 pb-3 space-y-1">
                        <responsive-nav-link :href="route('main')">
                            Vehicle List
                        </responsive-nav-link>
                        <responsive-nav-link :href="route('contact')">
                            Contact Us
                        </responsive-nav-link>

                        <responsive-nav-link
                            v-if="$page.props.auth.user"
                            :href="route('dashboard')"
                        >
                            User Panel
                        </responsive-nav-link>
                        <responsive-nav-link
                            v-if="$page.props.auth.user"
                            :href="route('logout')"
                            method="post"
                            as="button"
                        >
                            Log Out
                        </responsive-nav-link>

                        <responsive-nav-link
                            v-if="!$page.props.auth.user"
                            :href="route('login')"
                        >
                            Log In
                        </responsive-nav-link>
                    </div>
                </div>
            </nav>

            <!-- Page Heading -->
            <header class="bg-primary shadow" v-if="$slots.header">
                <div
                    class="
                        max-w-7xl
                        mx-auto
                        px-4
                        h-16
                        flex
                        items-center
                        sm:px-6
                        lg:px-8
                    "
                >
                    <slot name="header" />
                </div>
            </header>

            <!-- Page Content -->
            <main>
                <slot />
            </main>

            <!-- Back to Top Button -->
            <transition name="slide-in" class="hidden lg:block">
                <button
                    class="
                        fixed
                        bottom-10
                        right-10
                        z-10
                        flex
                        justify-center
                        items-center
                        flex-wrap
                        h-20
                        w-20
                        py-2
                        px-4
                        rounded-full
                        bg-secondary
                        shadow-md
                        text-white text-xs
                        focus:outline-none
                    "
                    v-if="showBackToTopButton"
                    @click="scrollToTop"
                >
                    <span class="text-xl icon-np-long-arrow-up"></span>
                    Back to Top
                </button>
            </transition>

            <!-- footer -->
            <footer class="hidden lg:block bg-secondary bottom-0">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div class="flex justify-between">
                        <div class="flex mt-10 mb-8">
                            <!-- Logo -->
                            <div class="flex-shrink-0 flex items-center">
                                <inertia-link :href="route('main')">
                                    <application-logo
                                        class="block h-9 w-auto"
                                    />
                                </inertia-link>
                            </div>

                            <!-- Navigation Links -->
                            <div class="sm:ml-10 sm:flex">
                                <inertia-link
                                    :href="route('main')"
                                    :class="footerLinkClasses"
                                >
                                    Vehicle List
                                </inertia-link>
                                <inertia-link
                                    :href="route('contact')"
                                    :class="footerLinkClasses"
                                >
                                    Contact Us
                                </inertia-link>
                            </div>
                        </div>
                        <div
                            class="
                                text-gray-300 text-xs
                                font-light
                                tracking-wide
                                mt-10
                                mb-8
                                inline-flex
                                items-center
                            "
                        >
                            © 2021 Birchwood
                        </div>
                    </div>
                </div>
            </footer>

            <footer class="md:hidden bg-secondary bottom-0">
                <div class="max-w-7xl mx-auto px-4 sm:px-6">
                    <div class="flex justify-between w-full">
                        <div
                            class="
                                text-gray-300 text-xs
                                font-light
                                tracking-wide
                                mt-8
                                mb-8
                                inline-flex
                                items-center
                                justify-center
                                w-full
                            "
                        >
                            © 2021 Birchwood
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    </div>
</template>

<style scoped>
.slide-in-leave-active,
.slide-in-enter-active {
    transition: 0.3s linear;
}

.slide-in-enter-from {
    transform: translate(100%, 0);
}

.slide-in-leave-to {
    transform: translate(100%, 0);
}
</style>

<script>
import ApplicationLogo from "@/Components/ApplicationLogo";
import ButtonComponent from "@/Components/Button";
import DropdownComponent from "@/Components/Dropdown";
import DropdownLink from "@/Components/DropdownLink";
import NavLink from "@/Components/NavLink";
import ResponsiveNavLink from "@/Components/ResponsiveNavLink";

export default {
    components: {
        ApplicationLogo,
        ButtonComponent,
        DropdownComponent,
        DropdownLink,
        NavLink,
        ResponsiveNavLink,
    },
    data() {
        return {
            showingNavigationDropdown: false,
            lastScrollTop: 0,
            showBackToTopButton: false,
            footerLinkClasses: [
                "inline-flex items-center px-6 text-xs font-light tracking-wide text-white hover:text-primary transition duration-500",
            ],
        };
    },
    mounted() {
        // show Back to Top button when scrolling up
        document.addEventListener("scroll", this.handleScroll);
    },
    methods: {
        handleScroll() {
            let st = window.pageYOffset || document.documentElement.scrollTop;
            this.showBackToTopButton = st < this.lastScrollTop && st > 0;
            this.lastScrollTop = st <= 0 ? 0 : st;
        },
        scrollToTop() {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        },
    },
};
</script>
